<template>
  <div class="flex flex-col w-full h-full gap-2 overflow-y-auto">
    <div class="flex flex-col gap-1 mb-2">
      <span class="text-sm text-gray-600">שיוך כרטיסייה ל-</span>
      <span class="font-medium">{{ customer.fullName }}</span>
    </div>

    <span class="mb-1 font-medium">בחירת כרטיסייה</span>
    <div v-if="loadingTemplates" class="flex justify-center w-full p-4">
      <loader />
    </div>
    <div v-else-if="templates.length === 0" class="text-sm text-gray-500 p-2">
      לא הוגדרו כרטיסיות. צרו כרטיסייה במסך "שוברים וכרטיסיות".
    </div>
    <div v-else class="flex flex-col gap-2">
      <div
        v-for="template of templates"
        :key="template._id"
        class="border rounded-md p-3 cursor-pointer transition-colors"
        :class="selectedTemplateId === template._id ? 'border-primary-500 bg-primary-50' : 'bg-white hover:bg-gray-50'"
        @click="selectedTemplateId = template._id"
      >
        <div class="flex items-center gap-2">
          <i class="pi pi-id-card text-primary-700" />
          <span class="font-medium">{{ template.name }}</span>
        </div>
        <span class="text-xs text-gray-600 block mt-1">
          {{ template.totalUses }} מימושים · {{ template.price }} ₪
          <template v-if="template.validityMonths">
            · תוקף {{ template.validityMonths }} חודשים
          </template>
        </span>
      </div>
    </div>

    <drawer-footer
      class="mt-auto"
      :approveDisable="!selectedTemplateId || assigning"
      :approveLoading="assigning"
      approveLabel="שיוך והמשך לתשלום"
      @approve-click="assign"
      @cancel-click="$emit('close')"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, PropType, ref } from 'vue';
import { IBundlePackTemplate, ICustomer } from '@torim-app/types';
import { BundlePacksApi } from '@app/api/admin/bundle-packs-api';
import { useToast } from 'vue-toastification';
import { useDrawer } from '@torim-app/shared-components';
import PaymentOptions from '@app/components/admin/modals/PaymentOptions.vue';

const props = defineProps({
  customer: {
    type: Object as PropType<ICustomer>,
    required: true
  }
});

const emit = defineEmits(['assigned', 'close']);
const toast = useToast();
const { openDrawer, closeDrawer } = useDrawer();

const templates = ref<IBundlePackTemplate[]>([]);
const loadingTemplates = ref(false);
const selectedTemplateId = ref<string | undefined>(undefined);
const assigning = ref(false);

const fetchTemplates = async () => {
  loadingTemplates.value = true;
  try {
    const all = await BundlePacksApi.fetchTemplates();
    templates.value = all.filter(t => t.active !== false);
  } catch (e) {
    toast.error('שגיאה בטעינת הכרטיסיות');
  } finally {
    loadingTemplates.value = false;
  }
};

const assign = async () => {
  if (!selectedTemplateId.value) return;
  assigning.value = true;
  try {
    const created = await BundlePacksApi.assignPack({
      templateId: selectedTemplateId.value,
      customerId: props.customer.id
    });
    toast.success('הכרטיסייה שויכה');
    emit('assigned', created);
    openDrawer(
      PaymentOptions,
      {
        uuid: created._id,
        payFor: 'bundle-pack',
        onClose: (shouldLoad: boolean) => {
          closeDrawer();
          if (shouldLoad) emit('assigned', created);
          emit('close');
        }
      },
      { title: 'אפשרויות תשלום' }
    );
  } catch (e) {
    console.error(e);
    toast.error('שגיאה בשיוך הכרטיסייה');
  } finally {
    assigning.value = false;
  }
};

onMounted(fetchTemplates);
</script>
